import { useState } from "react";
import { toast } from "sonner";
import { useContent } from "@/context/ContentContext";
import { resolveImage } from "@/lib/api";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { Mail, Phone, MapPin } from "lucide-react";

export default function Contact() {
  const { content } = useContent();
  const contact = content?.contact || {};
  const heroImg = content?.page_images?.contact_hero;
  const [form, setForm] = useState({ name: "", email: "", phone: "", org: "", message: "" });
  const [busy, setBusy] = useState(false);

  const setF = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/enquiries", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        organization: form.org,
        message: form.message,
        source: "contact",
      });
      toast.success("Thank you! Our sales team will reach out within 24 hours.");
      setForm({ name: "", email: "", phone: "", org: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send enquiry. Please try WhatsApp.");
    } finally {
      setBusy(false);
    }
  };

  const waHref = contact.whatsapp_number
    ? `https://wa.me/${(contact.whatsapp_number || "").replace(/\D/g, "")}?text=${encodeURIComponent(contact.whatsapp_message || "Hi TMF")}`
    : "#";

  return (
    <div className="max-w-7xl mx-auto px-4 py-16 animate-tmf-fade">
      <div className="border-b border-slate-200 pb-6 mb-12">
        <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-tmf-blue mb-2">Contact</div>
        <h1 className="font-display font-black text-4xl md:text-6xl uppercase tracking-tight text-tmf-navy">Talk to the factory.</h1>
      </div>

      {heroImg && (
        <div className="aspect-[16/6] w-full overflow-hidden border border-slate-200 mb-12">
          <img src={resolveImage(heroImg)} alt="Contact TMF" className="w-full h-full object-cover" />
        </div>
      )}

      <div className="grid lg:grid-cols-12 gap-10">
        <form onSubmit={submit} className="lg:col-span-7 border border-slate-200 p-8 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500">Full Name</label>
              <Input data-testid="contact-name" required value={form.name} onChange={setF("name")} className="rounded-none mt-1" />
            </div>
            <div>
              <label className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500">Organization</label>
              <Input data-testid="contact-org" value={form.org} onChange={setF("org")} className="rounded-none mt-1" />
            </div>
            <div>
              <label className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500">Email</label>
              <Input data-testid="contact-email" required type="email" value={form.email} onChange={setF("email")} className="rounded-none mt-1" />
            </div>
            <div>
              <label className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500">Phone</label>
              <Input data-testid="contact-phone" required value={form.phone} onChange={setF("phone")} className="rounded-none mt-1" />
            </div>
          </div>
          <div>
            <label className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500">How can we help?</label>
            <Textarea data-testid="contact-message" required rows={6} value={form.message} onChange={setF("message")} className="rounded-none mt-1" placeholder="Product interests, quantities, delivery timelines, etc." />
          </div>
          <Button data-testid="contact-submit" type="submit" disabled={busy} className="rounded-none bg-tmf-blue hover:bg-tmf-navy h-12 px-6 uppercase tracking-widest text-sm font-bold w-full md:w-auto">
            {busy ? "Sending…" : "Send Enquiry"}
          </Button>
        </form>

        <aside className="lg:col-span-5 space-y-4">
          <div className="border border-tmf-navy p-6 bg-tmf-navy text-white">
            <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-tmf-sky mb-2">Direct Line</div>
            <div className="font-display font-black text-3xl uppercase leading-tight">Bulk & OEM</div>
            <div className="mt-4 space-y-3 text-sm">
              <div className="flex gap-3"><Mail size={16} className="text-tmf-sky shrink-0 mt-0.5" /> {contact.email}</div>
              {contact.phone && <div className="flex gap-3"><Phone size={16} className="text-tmf-sky shrink-0 mt-0.5" /> {contact.phone}</div>}
              <a href={waHref} target="_blank" rel="noreferrer" className="flex gap-3 hover:text-tmf-sky items-center"><WhatsAppIcon size={16} className="text-[#25D366] shrink-0" /> WhatsApp: {contact.whatsapp_number}</a>
              <div className="flex gap-3"><MapPin size={16} className="text-tmf-sky shrink-0 mt-0.5" /> {contact.address}</div>
            </div>
          </div>
          <div className="border border-slate-200 p-6">
            <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-tmf-blue mb-1">Response Time</div>
            <div className="font-display font-bold text-lg">Under 24 hours</div>
            <p className="text-sm text-slate-600 mt-2">Bulk quotes typically ready same-day, Mon–Sat, 9am–7pm IST.</p>
          </div>
        </aside>
      </div>
    </div>
  );
}
